import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import { logger } from "@/lib/logger";

/**
 * Lightweight guard: is ANY AI provider currently enabled?
 *
 * WP4 (TES-1128) capability gate. The chat UI uses this to decide whether to
 * render the AI panel / Help button or fall back to the offline/no-provider
 * state (AC: "kein aktiver Provider → CapabilityWarning/Offline-Banner").
 *
 * Reads the `has_active_ai_provider()` RPC (restored in migration 263,
 * SECURITY DEFINER + GRANT EXECUTE TO authenticated) so it works for EVERY
 * logged-in user — not only Super-Admins (the `manage-ai-config` Edge Function
 * is admin-only and would 403 for regular users). On any RPC error (e.g. the
 * function not yet deployed) we degrade to `false` instead of crashing.
 *
 * The RPC only runs when at least one AI feature flag is on, so a fully
 * disabled AI feature never fires the request.
 */
export function useHasActiveAiProvider() {
  const { enabled: assistantEnabled } = useFeatureFlag(
    "ai-assistant",
    FEATURE_FLAGS["ai-assistant"] ?? false,
  );
  const { enabled: helpChatEnabled } = useFeatureFlag(
    "help-chat",
    FEATURE_FLAGS["help-chat"] ?? false,
  );
  const anyAiFeatureEnabled = assistantEnabled || helpChatEnabled;

  return useQuery({
    queryKey: ["has-active-ai-provider"],
    queryFn: async () => {
      // The RPC isn't in the generated frontend types (DB-only table); cast.
      const { data, error } = await (
        supabase.rpc as unknown as (
          fn: string,
        ) => Promise<{ data: boolean | null; error: { message: string } | null }>
      )("has_active_ai_provider");

      if (error) {
        logger.debug("[useHasActiveAiProvider] RPC failed:", error.message);
        return false;
      }
      return data === true;
    },
    enabled: anyAiFeatureEnabled,
    staleTime: 5 * 60 * 1000, // 5 min — provider config changes rarely
    retry: false,
  });
}
